import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, ChevronDown, LogIn, UserPlus, Settings, Heart, LogOut, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();
  const isAuthenticated = !!user;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinkClass = "text-white/80 hover:text-naples-400 font-medium transition-colors duration-200 text-[0.95rem]";

  return (
    <header className="bg-charcoal-500/95 backdrop-blur-md sticky top-0 z-50 border-b border-white/5">
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <span
              className="text-naples-400 group-hover:text-naples-300 transition-colors duration-200"
              style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 600, fontSize: '1.4rem', lineHeight: 1.1, letterSpacing: '-0.01em' }}
            >
              <span style={{ fontSize: '0.7em', display: 'block', fontWeight: 500, opacity: 0.85 }}>The</span>
              <span style={{ fontSize: '1em' }}>Travel Atlas</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={navLinkClass}>Home</Link>
            <Link to="/designer" className={navLinkClass}>My Trips</Link>
            <Link to="/atlas" className={navLinkClass}>Atlas Files</Link>
            <Link to="/atlas-plan" className="flex items-center gap-1.5 px-3 py-1.5 bg-coral-400 hover:bg-coral-500 text-white font-semibold text-sm rounded-lg transition-colors">
              Atlas Plan
            </Link>
            {isAdmin && (
              <Link to="/admin" className="flex items-center gap-1.5 text-naples-400 hover:text-naples-300 font-medium transition-colors duration-200 text-[0.95rem]">
                <Shield size={14} /> Admin
              </Link>
            )}

            {/* Profile Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className={`flex items-center gap-2 ${navLinkClass}`}
              >
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <User size={16} />
                </div>
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-elevated border border-platinum-100 py-2 z-50 overflow-hidden"
                  >
                    {isAuthenticated ? (
                      <>
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-5 py-3 text-charcoal-500 hover:bg-platinum-50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <User size={16} className="text-charcoal-300" /> Account
                        </Link>
                        <Link
                          to="/designer"
                          className="flex items-center gap-3 px-5 py-3 text-charcoal-500 hover:bg-platinum-50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <Heart size={16} className="text-charcoal-300" /> Saved Trips
                        </Link>
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-5 py-3 text-charcoal-500 hover:bg-platinum-50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <Settings size={16} className="text-charcoal-300" /> Settings
                        </Link>
                        <div className="border-t border-platinum-200 my-1.5" />
                        <button
                          className="flex items-center gap-3 px-5 py-3 text-red-500 hover:bg-red-50 transition-colors w-full"
                          onClick={() => { setIsProfileOpen(false); signOut(); navigate('/'); }}
                        >
                          <LogOut size={16} /> Log Out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="flex items-center gap-3 px-5 py-3 text-charcoal-500 hover:bg-platinum-50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <LogIn size={16} className="text-charcoal-300" /> Sign In
                        </Link>
                        <Link
                          to="/register"
                          className="flex items-center gap-3 px-5 py-3 text-charcoal-500 hover:bg-platinum-50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <UserPlus size={16} className="text-charcoal-300" /> Sign Up
                        </Link>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white hover:text-naples-400 transition-colors w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
            >
              <div className="mt-4 pb-4 space-y-1">
                <Link to="/" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>Home</Link>
                <Link to="/designer" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>My Trips</Link>
                <Link to="/atlas" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>Atlas Files</Link>
                <Link to="/atlas-plan" className="block py-3 px-3 rounded-xl text-coral-400 hover:text-coral-300 font-semibold transition-colors text-[0.95rem]" onClick={() => setIsMenuOpen(false)}>Atlas Plan</Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-2 py-3 px-3 rounded-xl hover:bg-white/5 text-naples-400 hover:text-naples-300 font-medium transition-colors duration-200 text-[0.95rem]" onClick={() => setIsMenuOpen(false)}>
                    <Shield size={14} /> Admin
                  </Link>
                )}

                <div className="border-t border-white/10 pt-3 mt-3">
                  <p className="text-xs text-white/40 mb-2 px-3 uppercase tracking-wider font-medium">
                    My Profile
                  </p>
                  {isAuthenticated ? (
                    <>
                      <Link to="/profile" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>Account</Link>
                      <Link to="/designer" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>Saved Trips</Link>
                      <button className="block text-red-400 hover:text-red-300 font-medium py-3 px-3 rounded-xl hover:bg-white/5 w-full text-left" onClick={() => { setIsMenuOpen(false); signOut(); navigate('/'); }}>Log Out</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>Sign In</Link>
                      <Link to="/register" className={`block py-3 px-3 rounded-xl hover:bg-white/5 ${navLinkClass}`} onClick={() => setIsMenuOpen(false)}>Sign Up</Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}

export default Header;
